import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ShoppingCart, Search as SearchIcon, User as UserIcon, LogOut, Receipt } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { OwlLogo } from './OwlLogo';
import { AlertBell } from './AlertBell';
import { authApi, cartApi } from '@/lib/api';
import { useUiStore } from '@/stores/ui';
import { useCurrentUser } from '@/lib/auth';
import { toast } from './ui/sonner';

const CATEGORY_NAV: { label: string; slug: string }[] = [
  { label: 'Wandcraft', slug: 'Wandcraft' },
  { label: 'Apothecary', slug: 'Apothecary' },
  { label: 'Quidditch', slug: 'Quidditch' },
  { label: 'Familiars', slug: 'Familiars' },
  { label: 'Confectionery', slug: 'Confectionery' },
  { label: 'Books', slug: 'Books & Scrolls' },
];

export function Header() {
  const navigate = useNavigate();
  const openCart = useUiStore((s) => s.openCart);
  const [q, setQ] = useState('');

  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: async () => (await cartApi.get()).data,
    refetchOnWindowFocus: false,
  });
  const cartCount = (cart.data?.items ?? []).reduce((n, it) => n + it.quantity, 0);

  const me = useCurrentUser();
  const qc = useQueryClient();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close user menu on outside click / Escape.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const logoutMut = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(['auth', 'me'], null);
      qc.invalidateQueries({ queryKey: ['cart'] });
      toast.success('Owl returned. Signed out.');
      setUserMenuOpen(false);
      navigate('/');
    },
  });

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  }

  return (
    <header className="border-b border-(--color-galleon-gold)/40 bg-(--color-parchment) sticky top-0 z-40">
      <div className="mx-auto max-w-6xl px-4 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Diagon Alley home">
          <OwlLogo size={36} />
          <span className="font-display text-xl tracking-wide hidden sm:inline">Diagon Alley</span>
        </Link>

        <nav aria-label="Categories" className="hidden md:flex items-center gap-1 ml-2">
          {CATEGORY_NAV.map((c) => (
            <Link
              key={c.slug}
              to={`/c/${encodeURIComponent(c.slug)}`}
              className="px-3 py-1.5 text-sm rounded-md hover:bg-(--color-galleon-gold)/20 transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={onSearch} className="ml-auto flex items-center" role="search">
          <label htmlFor="header-search" className="sr-only">Search</label>
          <div className="relative">
            <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-(--color-ink-muted)" aria-hidden="true" />
            <Input
              id="header-search"
              placeholder="Search the Alley…"
              className="pl-8 w-44 sm:w-56"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </form>

        <AlertBell />

        {me.data ? (
          <div ref={userMenuRef} className="relative">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label={`Signed in as ${me.data.email}`}
              onClick={() => setUserMenuOpen((o) => !o)}
            >
              <UserIcon className="h-5 w-5" />
            </Button>
            {userMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-56 rounded-md border bg-card shadow-md py-1 z-50"
              >
                <div className="px-3 py-2 text-xs text-(--color-ink-muted) border-b">
                  Signed in as<br />
                  <span className="font-medium text-(--color-ink) break-all">{me.data.email}</span>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-(--color-galleon-gold)/15 flex items-center gap-2"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/orders');
                  }}
                >
                  <Receipt className="h-4 w-4" aria-hidden="true" />
                  Orders
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-(--color-galleon-gold)/15 flex items-center gap-2 text-destructive"
                  disabled={logoutMut.isPending}
                  onClick={() => logoutMut.mutate()}
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  {logoutMut.isPending ? 'Signing out…' : 'Sign out'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Sign in"
            onClick={() => navigate('/login')}
          >
            <UserIcon className="h-5 w-5" />
          </Button>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Cart (${cartCount} items)`}
          className="relative"
          onClick={openCart}
        >
          <ShoppingCart className="h-5 w-5" />
          {cartCount > 0 && (
            <Badge
              variant="gold"
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-xs flex items-center justify-center"
            >
              {cartCount}
            </Badge>
          )}
        </Button>
      </div>
    </header>
  );
}
